import React, { ReactNode, useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { MaterialTopTabNavigationEventMap, createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { ScreenListeners, TabNavigationState } from '@react-navigation/native';

import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { NEW_TOP_TAB_BAR_CONFIG, TopTabBar, TopTabBarProps } from '@/components/TopTabBar';
import { BetsTabParamsList } from '@/feature/bets-sbk/screens/BetsScreen';
import { EntriesTabParamsList } from '@/feature/entries-pickem/screens/EntriesTopTabBarScreen';
import { PromohubTabParamList } from '@/feature/promohub/screens/Promohub';
import { common } from '@/styles/styles';

import { Box, Row } from './lib/components';
import { ds } from './lib/design.system';

type TabListenerType = ScreenListeners<TabNavigationState<any>, MaterialTopTabNavigationEventMap>;

// known warning from react-native 0.72+ regarding `onAnimatedValueUpdate` with no exact fix for tabs
// https://github.com/react-navigation/react-navigation/issues/7839

const NAV_BAR_HEIGHT = 56;

const edges = [] as const;

export const HeaderNavTabs = <T extends EntriesTabParamsList | PromohubTabParamList | BetsTabParamsList>({
    title,
    mode,
    customTabsListeners,
    children,
    initialRouteName,
    rightAccessory,
    tabBarScrollEnabled = false,
}: {
    title: string;
    mode?: 'stack' | 'tabs' | undefined;
    customTabsListeners?: TabListenerType;
    children: ReactNode;
    initialRouteName: Extract<keyof T, string>;
    rightAccessory?: ReactNode;
    tabBarScrollEnabled?: boolean;
}) => {
    // Create Tab navigator with generic type
    const Tab = useMemo(() => createMaterialTopTabNavigator<T>(), []);
    const screenOptions = useMemo(
        () => (tabBarScrollEnabled ? { ...NEW_TOP_TAB_BAR_CONFIG, tabBarScrollEnabled } : NEW_TOP_TAB_BAR_CONFIG),
        [tabBarScrollEnabled]
    );

    const renderTabBar = useCallback((props: TopTabBarProps) => {
        return <TopTabBar {...props} customEdges={edges} separatorStyles={styles.tabSeparator} />;
    }, []);

    return (
        <Screen edges={['top']}>
            <Box style={[common.noOverflow, common.flex]}>
                {mode === 'stack' ? (
                    <ScreenNavBar
                        title={title}
                        titleOverrideStyle={styles.title}
                        closeIconMode={'back'}
                        style={styles.screenNavBarContainer}
                    />
                ) : (
                    <Row
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        paddingHorizontal={'s16'}
                        height={NAV_BAR_HEIGHT}
                    >
                        <Text
                            color={ds.colorToken.text.primary}
                            fontWeight={'700'}
                            fontSize={24}
                            lineHeight={32}
                            letterSpacing={0.07}
                        >
                            {title}
                        </Text>
                        {rightAccessory}
                    </Row>
                )}
                <Tab.Navigator
                    screenListeners={customTabsListeners}
                    tabBar={renderTabBar}
                    initialRouteName={initialRouteName}
                    screenOptions={screenOptions}
                >
                    {children}
                </Tab.Navigator>
            </Box>
        </Screen>
    );
};

const styles = StyleSheet.create({
    tabSeparator: { width: 0 },
    screenNavBarContainer: {
        height: NAV_BAR_HEIGHT,
    },
    title: {
        color: ds.colorValue.text.primary,
        textAlign: 'center',
        fontSize: 18,
        fontWeight: '600',
        lineHeight: 28,
        letterSpacing: -0.44,
    },
});
